module Meevo.Plugins.Register {
    "use strict";

    export class RegisterSectionBase {
        refundSubTotal: number = 0;
        refundTax: number = 0;
		refundTotal: number = 0;
        
		protected template: string;

        constructor(public sectionType: Meevo.Plugins.Register.RegisterSectionTypeEnum,
            protected registerContext: Meevo.Plugins.Register.RegisterContext,
            sectionPath: string,
            public sectionCatType: Register.RegisterSectionCatTypeEnum) {
            this.template = sectionPath;
        }

        reset() {
        }

        edit(saleLine: any) {
        }

        delete(saleLine) {
        }

        hasItems() {
            return false;
        }

        isDirty() {
            return false;
        }   

        clientSelected() {
            
        }

        switchToServicesWhenClientSelected() {
            return true;
        }

        canApplyRefund(): boolean {
            return this.refundTotal > 0;
        }

        getRefundEmployeeDisplayName(saleLine) {
            if (saleLine && saleLine.employeeDisplayName) {
                return saleLine.employeeDisplayName;
            }
            else {
                return this.registerContext.HouseEmployee.displayName;
            }
        }

        // The in-progress (temp) sale line being built before it's committed to the receipt.
        protected getTempSaleLine(): any {
            return this.registerContext.tempSaleLines && this.registerContext.tempSaleLines.length
                ? this.registerContext.tempSaleLines[0]
                : null;
        }

        // Lot rows on the temp line that have a lot chosen.
        // whenever a lot is picked, so it alone identifies a selected row.
        protected getSelectedLotAllocations(): any[] {
            const tempLine = this.getTempSaleLine();
            const item: any = tempLine ? tempLine.item : null;
            return (item && item.lotAllocations)
                ? _.filter(item.lotAllocations, (l: any) => !!l.variantLotId)
                : [];
        }

        protected getSelectedLotQuantityTotal(): number {
            return _.reduce(this.getSelectedLotAllocations(), (sum: number, l: any) => sum + (+l.quantity || 0), 0);
        }

        getManualRefundMessage(historicalSaleLine) {
            let dateFilter: any = this.registerContext.$filter("meevoDateFilter");

            const params: any = {
                date: dateFilter(historicalSaleLine.saleDate),
                employeeName: this.getRefundEmployeeDisplayName(historicalSaleLine),
                price: this.registerContext.meevoCurrency(historicalSaleLine.finalItemPriceCharge),
                quantity: historicalSaleLine.quantity,
                itemType: historicalSaleLine.saleType === Model.SalesTypeEnum.Service ?
                    this.registerContext.appSettings.APP_FIELDLABELS["service"] :
                    this.registerContext.appSettings.APP_FIELDLABELS["product"]
            };

            const selectedLots = this.getSelectedLotAllocations();
            if (selectedLots.length) {
                params.lotNumber = _.map(selectedLots, (l: any) => l.variantLot.lotNumber).join(", ");
                params.quantity = this.getSelectedLotQuantityTotal();
                return this.registerContext.$translate.instant("MESSAGE_MANUAL_REFUND_WITH_LOT", params);
            }

            return this.registerContext.$translate.instant("MESSAGE_MANUAL_REFUND", params);
        }

        addRefundSaleLine(id, saleType, localReset) {
            // For lot-tracked manual refunds, narrow the historical match to sales that still
            // have the selected lots available in the selected quantities.
            const tempLine: any = this.getTempSaleLine();
            const selectedLotAllocations = this.getSelectedLotAllocations();
            const selectedLotQtyTotal = this.getSelectedLotQuantityTotal();
            const lots = _.map(selectedLotAllocations, (l: any) => ({ variantLotId: l.variantLotId, quantity: l.quantity }));
            const cloneSelectedLots = () => _.map(selectedLotAllocations, (l: any) => ({
                variantLotId: l.variantLotId,
                variantLot: l.variantLot,
                quantity: l.quantity
            }));

            // Carry the lot selections onto the temp line before it's committed via employee
            // selection: quantity = sum of selected lot qtys, and drop any unselected (empty) lot
            // rows so the cart doesn't show a stray row or a false lot-qty mismatch.
            const finalizeManualLotLine = () => {
                if (tempLine && selectedLotAllocations.length) {
                    tempLine.quantity = selectedLotQtyTotal;
                    tempLine.item.lotAllocations = cloneSelectedLots();
                }
            };

            const applyLotScreenSelectionsToRefundLine = (historicalSaleLine: any) => {
                if (!selectedLotAllocations.length) {
                    return;
                }
                const addedLine: any = _.last(_.filter(this.registerContext.sale.saleLines, (sl: any) =>
                    sl.isRefund &&
                    sl.refundSaleLineId === historicalSaleLine.id &&
                    sl.refundSaleId === historicalSaleLine.saleId &&
                    sl.refundLocationId === historicalSaleLine.locationId));
                if (addedLine && addedLine.item) {
                    addedLine.item.lotAllocations = cloneSelectedLots();
                }
            };

            // The historical match is keyed by client, so there's nothing to search for an
            // anonymous client. Skip straight to the "no match" outcome: carry the selected lots
            // onto the temp line (qty = sum of lot qtys) and go to employee selection.
            if (!this.registerContext.selectedClient) {
                finalizeManualLotLine();
                this.registerContext.selectSection(RegisterSectionTypeEnum.Employee);
                return;
            }

            const request = {
                criteria: {
                    clientId: this.registerContext.selectedClient.id,
                    itemId: id,
                    saleType: saleType,
                    lots: lots
                },
                itemsPerPage: 1000,
                sortDirection: Model.SortDirection.Descending,
            };

            this.registerContext.dataService.getListPaged("SaleLine", request).then((data: any) => {
                let historicalSaleLine = data.shift();
                while (historicalSaleLine) {
                    let matchingRefundSaleLine = _(this.registerContext.sale.saleLines).
                        findWhere({ refundTransactionId: historicalSaleLine.transactionId });
                    if (!matchingRefundSaleLine) {
                        break;
                    }
                    historicalSaleLine = data.shift();
                }
                if (historicalSaleLine) {
                    this.registerContext.modalService.confirmHandler(this.getManualRefundMessage(historicalSaleLine),
                        Components.Modal.ModalTranslationModes.DoNotTranslate).then(confirmResponse => {
                            if (confirmResponse) {
                                if (selectedLotQtyTotal > 0) {
                                    historicalSaleLine.newQuantity = selectedLotQtyTotal;
                                }
                                this.registerContext.addRefundSaleLine(historicalSaleLine, saleType);
                                applyLotScreenSelectionsToRefundLine(historicalSaleLine);
                                this.registerContext.recalculateSale()
                                    .then(() => {
                                        localReset();
                                    })
                                    .catch(() => {
                                        this.registerContext.selectSection(this.registerContext.previouslySelectedSection.sectionType, false);
                                        this.registerContext.sale.saleLines.pop();
                                    });
                            }
                            else {
                                finalizeManualLotLine();
                                this.registerContext.selectSection(RegisterSectionTypeEnum.Employee);
                            }
                        })
                }
                else {
                    finalizeManualLotLine();
                    this.registerContext.selectSection(RegisterSectionTypeEnum.Employee);
                }
            });
        }

        goToTransactionEditor(transactionId: string) {
            this.registerContext.$rootScope.$broadcast("gotoState", {
                stateName: this.registerContext.appSettings.ROUTE_NAMES.transactionEditor,
                stateParams: {
                    transactionId: transactionId,
                    includeActive: true,
                    includeCompleted: true,
                    includeOnHold: false,
                    includeRefunded: true,
                    includeVoided: true
                },
                securityKey: Meevo.Components.Common.SecurityKeys.REGISTER_TRANSACTIONEDITOR + Meevo.Components.Common.SecurityParts.KEY_PART_READ,
            });
        }

        hasExternalTipTransactionIdMismatch(lines: any[]) {
            const ids = lines.map(line => line.externalTipTransactionId).filter(id => id);
            return new Set(ids).size > 1;
        }

        hasAuthorizationPaymentRequestIdMismatch(lines: any[]) {
            const ids = lines.map(line => line.authorizationPaymentRequestId).filter(id => id);
            return new Set(ids).size > 1;
        }

        isMeevoPayProvider(): boolean {
            return Meevo.Model.MerchantAccountProvider.isMeevoPayProvider(this.registerContext.getMerchantProviderId());
        }
    }
}